/**
 * Public page ??worker/expert confirms site visit invite (first accept wins).
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

function AmendSsVisitRespond() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/amend-ss-visit-public/${token}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) setError(d);
        else setData(d);
      })
      .catch((e) => setError({ error: e.message }))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = (action) => {
    if (!token) return;
    setSubmitting(true);
    fetch(`/api/amend-ss-visit-public/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.error) throw new Error(d.error);
        setDone(d);
      })
      .catch((e) => setError({ error: e.message }))
      .finally(() => setSubmitting(false));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-gray-600">Loading…</p>
      </div>
    );
  }

  if (error?.error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-xl shadow p-8 max-w-md text-center">
          <h1 className="text-lg font-semibold text-red-700 mb-2">Unable to open link</h1>
          <p className="text-gray-600 text-sm">{error.error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-xl shadow-lg p-8 max-w-md text-center">
          <h1 className="text-lg font-semibold text-emerald-800 mb-2">Thank you</h1>
          <p className="text-gray-700 text-sm">{done.message}</p>
        </div>
      </div>
    );
  }

  const { invite, request } = data || {};
  if (!invite || !request) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <p className="text-gray-600">Invalid link.</p>
      </div>
    );
  }

  if (invite.status !== 'PENDING') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-xl shadow p-8 max-w-md text-center">
          <h1 className="text-lg font-semibold text-gray-800 mb-2">Already responded</h1>
          <p className="text-gray-600 text-sm">This invite is no longer open.</p>
        </div>
      </div>
    );
  }

  if (request.requestStatus === 'SITE_VISIT_REQUESTED') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-card rounded-xl shadow p-8 max-w-md text-center">
          <h1 className="text-lg font-semibold text-amber-800 mb-2">Position filled</h1>
          <p className="text-gray-600 text-sm">
            Someone else has already been assigned for this site visit.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-lg p-8 max-w-lg w-full space-y-4">
        <h1 className="text-xl font-bold text-gray-900">Site visit request</h1>
        <p className="text-sm text-gray-600">
          Hi <strong>{invite.partyName}</strong> ??Cornerstone Building Service needs a{' '}
          <strong>{invite.partyType === 'EXPERT' ? 'expert (verification / COV path)' : 'field worker'}</strong> for:
        </p>
        <ul className="text-sm text-gray-800 space-y-1 border border-gray-100 rounded-lg p-3 bg-slate-50">
          <li>
            <span className="text-gray-500">Building:</span> {request.buildingName || '—'}
          </li>
          <li>
            <span className="text-gray-500">Address:</span> {request.address || '—'}
          </li>
          <li>
            <span className="text-gray-500">SS:</span> {request.ssCode}
          </li>
          <li>
            <span className="text-gray-500">Situation:</span> {request.situationKey}
          </li>
        </ul>
        <p className="text-xs text-gray-500">
          The first person who confirms is assigned. Others will be notified automatically if the slot is filled.
        </p>
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="button"
            disabled={submitting}
            onClick={() => submit('ACCEPT')}
            className="px-5 py-2.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? 'Sending…' : 'Confirm I will attend'}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => submit('DECLINE')}
            className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}

export default AmendSsVisitRespond;
